import { NgModel, UntypedFormGroup } from '@angular/forms';

export class ValidationHelper {
    public readonly email: string = 'email';
    public readonly confirmEmail: string = 'confirmEmail';
    public readonly username: string = 'username';
    public readonly password: string = 'password';
    public readonly confirmPassword: string = 'confirmPassword';
    public readonly firstName: string = 'firstName';
    public readonly lastName: string = 'lastName';
    public readonly dateOfBirth: string = 'dateOfBirth';
    public readonly phoneNumber: string = 'phoneNumber';
    public readonly studentId: string = 'studentId';
    public readonly termsAccepted: string = 'termsAccepted';
    private allowedEmailDomains: string[] = [];
    private consumerBirhtDateValidation = false;
    private errorMapping = new Map<string, { [name: string]: string }>();

    public setDependentValues(allowedEmailDomains: string[]): this {
        this.allowedEmailDomains = allowedEmailDomains;
        return this;
    }
    public isConsumerBirthDateValidation(consumerBirthDateValidation: boolean): this {
        this.consumerBirhtDateValidation = consumerBirthDateValidation;
        return this;
    }
    public setErrorMapping(): Map<string, { [name: string]: string }> {
        this.errorMapping = new Map<string, { [name: string]: string }>();
        this.errorMapping.set(this.email, {
            required: 'You must provide an email.',
            pattern: 'You must enter your e-mail address in the form of myemail@mydomain.com.',
            domain: `The only allowed email domains you may use are: ${this.allowedEmailDomains}`,
            maxLength: 'Your e-mail address cannot be more than 128 characters.'
        });
        let commonError = 'The confirmation email address you entered does not match the original email address.';
        this.errorMapping.set(this.confirmEmail, {
            required: commonError,
            validateEqual: commonError,
        });
        commonError = 'Your username must be between 8 and 64 characters.';
        this.errorMapping.set(this.username, {
            required: 'You must provide a username.',
            userExist: 'That username is already taken.',
            maxlength: commonError,
            minlength: commonError,
            pattern: 'Only letters, numbers, and some special characters("@", "_", ".", and "-") are allowed.'
        });
        this.errorMapping.set(this.password, {
            required: 'You must provide a password.',
            pattern: 'Your password must contain 8 characters, with one lower case letter, one upper case letter, one number, and no spaces.'
        });
        commonError = 'The confirmation password you entered does not match the original password.';
        this.errorMapping.set(this.confirmPassword, {
            required: commonError,
            validateEqual: commonError
        });
        this.errorMapping.set(this.firstName, {
            required: 'You must enter your first name.'
        });
        this.errorMapping.set(this.lastName, {
            required: 'You must enter your last name.'
        });
        if (this.consumerBirhtDateValidation) {
            this.errorMapping.set(this.dateOfBirth, {
                required: 'You must enter your date of birth.',
                maxError: 'You must be at least 14 years or older to join SponsoredScholar.'
            });
        } else {
            this.errorMapping.set(this.dateOfBirth, {
                required: 'You must enter your date of birth.',
                maxError: 'You must select a valid date of birth.'
            });
        }

        this.errorMapping.set(this.phoneNumber, {
            patternError: 'Please enter a valid mobile phone number in the format 1234567890 or (123)456-7890.'
        });
        this.errorMapping.set(this.studentId, {
            required: 'You must enter your student ID.',
            maxLength: 'Your Student ID cannot be more than 100 characters.',
            userExist: 'An account for the given information has already been created. Please login with your existing credentials or use the forgot username or password features to reset your account information.'
        });
        this.errorMapping.set(this.termsAccepted, {
            required: 'You must accept the privacy policy.'
        });

        return this.errorMapping;
    }

    public getValidationMessage(formGroup: UntypedFormGroup | NgModel, formControlName: string): string[] {
        const mapping = this.errorMapping.get(formControlName);
        const errorMessage: string[] = [];
        const formControl = formGroup instanceof UntypedFormGroup ? formGroup.get(formControlName) : formGroup;
        for (const key in mapping)
            if (Object.prototype.hasOwnProperty.call(mapping, key))
                if (formControl && formControl.hasError(key))
                    if (errorMessage.indexOf(mapping[key]) === -1)
                        errorMessage.push(mapping[key]);
        return errorMessage;
    }

    getErrorMessages(createAccountFormGroup: UntypedFormGroup, textField: string): string[] {

        switch (textField) {
            case this.email:
                return this.getValidationMessage(createAccountFormGroup, this.email);
            case this.confirmEmail:
                return this.getValidationMessage(createAccountFormGroup, this.confirmEmail);
            case this.username:
                return this.getValidationMessage(createAccountFormGroup, this.username);
            case this.password:
                return this.getValidationMessage(createAccountFormGroup, this.password);
            case this.confirmPassword:
                return this.getValidationMessage(createAccountFormGroup, this.confirmPassword);
            case this.firstName:
                return this.getValidationMessage(createAccountFormGroup, this.firstName);
            case this.lastName:
                return this.getValidationMessage(createAccountFormGroup, this.lastName);
            case this.dateOfBirth:
                return this.getValidationMessage(createAccountFormGroup, this.dateOfBirth);
            case this.phoneNumber:
                return this.getValidationMessage(createAccountFormGroup, this.phoneNumber);
            case this.studentId:
                return this.getValidationMessage(createAccountFormGroup, this.studentId);
            case this.termsAccepted:
                return this.getValidationMessage(createAccountFormGroup, this.termsAccepted);
            default:
                return [];
        }
    }
}
